import { useEffect, useRef, useState } from 'react';
import { loadViewerDocument, renderPageToCanvas, type PDFDocumentProxy } from '../lib/pdf-render.js';

/**
 * OutputPreview — renders the ACTUAL saved bytes with pdf.js, page by page.
 *
 * The editor canvas shows the original document plus CSS overlays (an
 * approximation of pending edits). This modal instead rasterizes the real save
 * output, so it is the ground truth of "what you'll download" — including
 * embedded text (e.g. Japanese) drawn as real glyphs, baked-in rotations, and
 * removed/reordered pages. It loads the bytes into its own pdf.js document and
 * destroys it on close, independent of the editor's viewer document.
 */
interface Props {
  readonly bytes: Uint8Array;
  readonly fileName: string;
  readonly onClose: () => void;
}

// Target CSS width for the rendered page; the page is scaled to fit this.
const FIT_WIDTH = 720;
const MAX_SCALE = 2;

export function OutputPreview({ bytes, fileName, onClose }: Props): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Load the saved bytes into a dedicated pdf.js document; destroy on unmount.
  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setError(null);
    setDoc(null);
    loadViewerDocument(bytes)
      .then((d) => {
        if (cancelled) {
          void d.destroy();
          return;
        }
        loaded = d;
        setDoc(d);
        setTotal(d.numPages);
        setPageNum(1);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
      if (loaded) void loaded.destroy();
    };
  }, [bytes]);

  // Render the current page at a fit-to-width scale. Rotation is omitted so
  // pdf.js uses the saved page's intrinsic /Rotate (the baked-in output state).
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    void (async () => {
      try {
        const page = await doc.getPage(pageNum);
        const base = page.getViewport({ scale: 1 });
        const scale = Math.min(MAX_SCALE, FIT_WIDTH / base.width);
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        await renderPageToCanvas(doc, pageNum, canvas, scale);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, pageNum]);

  // Esc closes; ←/→ page through.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') setPageNum((n) => Math.max(1, n - 1));
      else if (e.key === 'ArrowRight') setPageNum((n) => Math.min(total || 1, n + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total, onClose]);

  return (
    <div
      className="preview-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="preview-modal"
        role="dialog"
        aria-modal="true"
        aria-label="保存結果のプレビュー"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="preview-modal__head">
          <h2>プレビュー</h2>
          <span className="preview-modal__file">{fileName}</span>
          <span className="preview-modal__spacer" />
          <div className="preview-pager" role="group" aria-label="ページ送り">
            <button
              className="btn btn--icon"
              disabled={pageNum <= 1}
              onClick={() => setPageNum((n) => Math.max(1, n - 1))}
              aria-label="前のページ"
            >
              ◀
            </button>
            <span className="preview-pager__no">{total ? `${pageNum} / ${total}` : '…'}</span>
            <button
              className="btn btn--icon"
              disabled={pageNum >= total}
              onClick={() => setPageNum((n) => Math.min(total, n + 1))}
              aria-label="次のページ"
            >
              ▶
            </button>
          </div>
          <button className="btn" onClick={onClose} aria-label="閉じる" title="閉じる (Esc)">
            閉じる
          </button>
        </header>

        <div className="preview-modal__body">
          {error ? (
            <p className="preview-modal__error">プレビューを表示できません: {error}</p>
          ) : (
            <canvas ref={canvasRef} className="preview-canvas" />
          )}
        </div>

        <footer className="preview-modal__foot">
          保存される PDF を実際に描画した結果です（編集オーバーレイではなく出力バイトのレンダリング）。
        </footer>
      </div>
    </div>
  );
}
